import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertTriangle, CheckCircle, Clock, FileText, Wrench, Users, Building2 } from 'lucide-react';
import api from '../lib/api';
import { fmtDate } from '../lib/dateUtils';

export default function ScadenzePage() {
  const [deadlines, setDeadlines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterModule, setFilterModule] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const [deadlinesRes, summaryRes] = await Promise.all([
          api.get('/scadenze'),
          api.get('/scadenze/summary'),
        ]);
        setDeadlines(deadlinesRes.data);
        setSummary(summaryRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredDeadlines = deadlines.filter(d => {
    if (filterModule !== 'all' && d.module !== filterModule) return false;
    if (filterStatus !== 'all' && d.status !== filterStatus) return false;
    return true;
  });

  const statusBadge = (status, days) => {
    if (status === 'scaduto') {
      return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Scaduto ({Math.abs(days)}gg)</Badge>;
    }
    if (status === 'in_scadenza') {
      if (days <= 15) {
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{days}gg</Badge>;
      }
      return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{days}gg</Badge>;
    }
    return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">OK</Badge>;
  };

  const moduleIcon = (module) => {
    const icons = {
      documenti_personale: <FileText size={14} className="text-blue-600" />,
      corsi_personale: <Users size={14} className="text-purple-600" />,
      manutenzioni: <Wrench size={14} className="text-amber-600" />,
      documenti_attrezzature: <Wrench size={14} className="text-orange-600" />,
      documenti_aziendali: <Building2 size={14} className="text-slate-600" />,
    };
    return icons[module] || <FileText size={14} />;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Caricamento scadenze...</div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scadenze-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Dashboard Scadenze</h1>
      </div>

      {/* Semaforo Operativo */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                <CheckCircle size={24} className="text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-green-700">{summary.totale.valido}</p>
                <p className="text-sm text-green-600">Validi</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                <Clock size={24} className="text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-amber-700">{summary.totale.in_scadenza}</p>
                <p className="text-sm text-amber-600">In Scadenza</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center">
                <AlertTriangle size={24} className="text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-red-700">{summary.totale.scaduto}</p>
                <p className="text-sm text-red-600">Scaduti</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Summary per Modulo */}
      {summary && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Riepilogo per Categoria</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { key: 'documenti_personale', label: 'Doc. Personale', icon: FileText, color: 'blue' },
                { key: 'corsi_personale', label: 'Corsi', icon: Users, color: 'purple' },
                { key: 'manutenzioni', label: 'Manutenzioni', icon: Wrench, color: 'amber' },
                { key: 'documenti_attrezzature', label: 'Doc. Attrezzature', icon: Wrench, color: 'orange' },
                { key: 'documenti_aziendali', label: 'Doc. Aziendali', icon: Building2, color: 'slate' },
              ].map(({ key, label, icon: Icon, color }) => (
                <div key={key} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={14} className={`text-${color}-600`} />
                    <span className="text-xs font-medium text-slate-600">{label}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-green-600">{summary[key]?.valido || 0}</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-amber-600">{summary[key]?.in_scadenza || 0}</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-red-600">{summary[key]?.scaduto || 0}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtri */}
      <div className="flex flex-wrap gap-3">
        <Select value={filterModule} onValueChange={setFilterModule}>
          <SelectTrigger className="w-48" data-testid="filter-module">
            <SelectValue placeholder="Filtra per modulo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i moduli</SelectItem>
            <SelectItem value="documenti_personale">Doc. Personale</SelectItem>
            <SelectItem value="corsi_personale">Corsi</SelectItem>
            <SelectItem value="manutenzioni">Manutenzioni</SelectItem>
            <SelectItem value="documenti_attrezzature">Doc. Attrezzature</SelectItem>
            <SelectItem value="documenti_aziendali">Doc. Aziendali</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40" data-testid="filter-status">
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="scaduto">Scaduti</SelectItem>
            <SelectItem value="in_scadenza">In Scadenza</SelectItem>
            <SelectItem value="valido">Validi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabella Scadenze */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Modulo</TableHead>
                <TableHead>Titolo</TableHead>
                <TableHead>Riferimento</TableHead>
                <TableHead>Scadenza</TableHead>
                <TableHead>Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDeadlines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    Nessuna scadenza trovata
                  </TableCell>
                </TableRow>
              ) : filteredDeadlines.map((item) => (
                <TableRow key={`${item.module}-${item.id}`} className={`hover:bg-slate-50 ${item.status === 'scaduto' ? 'bg-red-50/30' : item.status === 'in_scadenza' && item.days_remaining <= 15 ? 'bg-amber-50/30' : ''}`}>
                  <TableCell>{moduleIcon(item.module)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{item.module_label}</TableCell>
                  <TableCell className="font-medium text-sm">{item.title}</TableCell>
                  <TableCell className="text-sm text-slate-600">{item.reference}</TableCell>
                  <TableCell className="text-sm">{fmtDate(item.expiry_date)}</TableCell>
                  <TableCell>{statusBadge(item.status, item.days_remaining)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
